import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Radio, Trash2, ShieldOff, ChevronRight, Download, ArrowDownWideNarrow } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useConfirm } from "../components/ConfirmDialog";
import { useAppNames } from "../hooks/use-app-names";
import { EmployeeRowsSkeleton } from "../components/PageSkeletons";
import PendingSyncBadge from "../components/PendingSyncBadge";

const scoreTone = (s) => (s == null ? "text-neutral-400" : s >= 85 ? "text-green-700" : s >= 70 ? "text-amber-600" : "text-red-600");

const PERMANENT_DELETE_WAIT_DAYS = 30;
const daysSinceRevoke = (emp) => (emp.revoked_at ? Math.floor((Date.now() - new Date(emp.revoked_at).getTime()) / 86400000) : null);
const canPermanentlyDelete = (emp) => emp.status === "inactive" && daysSinceRevoke(emp) >= PERMANENT_DELETE_WAIT_DAYS;

export default function Employees() {
  const confirm = useConfirm();
  const { appLabel } = useAppNames();
  const [employees, setEmployees] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [deviceFor, setDeviceFor] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", department: "", position: "" });
  const [unlinkedDevices, setUnlinkedDevices] = useState(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [busy, setBusy] = useState(false);
  const [sort, setSort] = useState("none");

  const load = () => {
    // status=all -- revoked employees stay visible (greyed out) until they're eligible
    // for permanent deletion, instead of disappearing the moment they're revoked.
    api.get("/employees?status=all").then(({ data }) => setEmployees(data)).catch(() => setEmployees([]));
    api.get("/departments").then(({ data }) => setDepartments(data)).catch(() => {});
  };
  useEffect(() => {
    load();
    // Background retries land on the server every 60s -- poll so a "Pending sync"
    // badge disappears on its own once one succeeds, no manual refresh needed.
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, []);

  const sortedEmployees = useMemo(() => {
    if (!employees || sort === "none") return employees;
    const dir = sort === "high_low" ? -1 : 1;
    return [...employees].sort((a, b) => {
      if (a.latest_score == null) return 1;
      if (b.latest_score == null) return -1;
      return (a.latest_score - b.latest_score) * dir;
    });
  }, [employees, sort]);

  const downloadAll = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/employees/reports/all?sort=${sort}`, "_blank");
  };

  const addEmployee = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/employees", form);
      if (data.metadata_sync?.status === "synced") {
        toast.success(`${form.name} added to ${form.department}`, { description: "Synced to local server" });
      } else {
        toast.success(`${form.name} added to ${form.department}`, { description: "Saved — will sync once the local server is back online" });
      }
      setAddOpen(false);
      setForm({ name: "", email: "", department: "", position: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const openAssignDevice = (emp) => {
    setDeviceFor(emp);
    setSelectedDeviceId("");
    setUnlinkedDevices(null);
    api.get("/devices").then(({ data }) => setUnlinkedDevices(data)).catch(() => setUnlinkedDevices([]));
  };

  const assignDevice = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const picked = unlinkedDevices.find((d) => d.id === selectedDeviceId);
      const pickedLabel = picked.serial_number || picked.device_address || picked.device_name;
      const { data } = await api.post(`/employees/${deviceFor.id}/device`, { device_id: selectedDeviceId });
      if (data.metadata_sync?.status === "synced") {
        toast.success(`Pendant ${pickedLabel} assigned`, { description: "Synced to local server" });
      } else {
        toast.success(`Pendant ${pickedLabel} assigned`, { description: "Saved — will sync once the local server is back online" });
      }
      setDeviceFor(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (emp) => {
    const ok = await confirm({
      title: `Revoke ${emp.name}?`,
      description: "They'll be marked inactive and hidden from active lists. Their score history is kept — you can permanently delete them 30 days from now.",
      confirmLabel: "Revoke",
      danger: true,
    });
    if (!ok) return;
    try {
      const { data } = await api.delete(`/employees/${emp.id}`);
      if (data.metadata_sync?.status === "synced") {
        toast.success(`${emp.name} revoked`, { description: "Local server notified" });
      } else {
        toast.success(`${emp.name} revoked`, { description: "Will notify the local server once it's back online" });
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const permanentlyDelete = async (emp) => {
    const ok = await confirm({
      title: `Permanently delete ${emp.name}?`,
      description: "This cannot be undone. Their score history will be permanently deleted.",
      confirmLabel: "Delete permanently",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/employees/${emp.id}/permanent`);
      toast.success(`${emp.name} permanently deleted`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-8 fade-up">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-light text-3xl tracking-tight" data-testid="employees-title">Employees</h1>
          <p className="text-sm text-neutral-500 mt-1">Your service team, their pendants and their scores.</p>
        </div>
        <button data-testid="add-employee-button" onClick={() => setAddOpen(true)}
          className="inline-flex items-center justify-center gap-2 bg-[#111] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-neutral-800 transition-colors whitespace-nowrap self-start">
          <Plus className="w-4 h-4 shrink-0" /> Add employee
        </button>
      </div>

      <div className="flex flex-row items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <ArrowDownWideNarrow className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="employee-sort-select" className="w-36 sm:w-48 h-9 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none" data-testid="sort-option-none">Sort: none</SelectItem>
              <SelectItem value="high_low" data-testid="sort-option-high-low">Score: high to low</SelectItem>
              <SelectItem value="low_high" data-testid="sort-option-low-high">Score: low to high</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <button data-testid="download-all-button" onClick={downloadAll} disabled={!employees?.length}
          className="inline-flex items-center justify-center gap-1.5 sm:gap-2 border border-neutral-300 rounded-full px-3 sm:px-4 py-2 text-xs font-medium hover:bg-[#111] hover:text-white hover:border-[#111] transition-colors disabled:opacity-40 disabled:pointer-events-none shrink-0 whitespace-nowrap">
          <Download className="w-3.5 h-3.5 shrink-0" />
          <span className="hidden sm:inline">Download all (PDF)</span>
          <span className="inline sm:hidden">Download PDF</span>
        </button>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead>
            <tr className="border-b border-neutral-200 text-left text-xs uppercase tracking-wide text-neutral-500">
              <th className="px-6 py-3.5 font-medium">Employee</th>
              <th className="px-4 py-3.5 font-medium">Department</th>
              <th className="px-4 py-3.5 font-medium">Device</th>
              <th className="px-4 py-3.5 font-medium">7-day score</th>
              <th className="px-4 py-3.5 font-medium">Sessions</th>
              <th className="px-4 py-3.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {employees === null && <EmployeeRowsSkeleton />}
            {sortedEmployees?.map((emp) => {
              const revoked = emp.status === "inactive";
              const daysLeft = revoked ? Math.max(0, PERMANENT_DELETE_WAIT_DAYS - (daysSinceRevoke(emp) ?? 0)) : null;
              return (
              <tr key={emp.id} data-testid={`employee-row-${emp.id}`}
                className={`hover:bg-neutral-50 transition-colors ${revoked ? "opacity-50" : ""}`}>
                <td className="px-6 py-4">
                  <Link to={`/console/employees/${emp.id}`} className="flex items-center gap-3 group" data-testid={`employee-link-${emp.id}`}>
                    <div className="w-8 h-8 rounded-full bg-neutral-100 border border-neutral-200 flex items-center justify-center text-xs font-medium text-neutral-700">
                      {emp.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                    </div>
                    <div>
                      <p className="font-medium group-hover:underline underline-offset-4 flex items-center gap-1.5">
                        {emp.name}
                        {revoked && (
                          <span className="text-[10px] uppercase tracking-wide bg-neutral-100 text-neutral-500 border border-neutral-200 rounded-full px-1.5 py-0.5">
                            Revoked
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-neutral-400">{emp.position || emp.email}</p>
                    </div>
                  </Link>
                </td>
                <td className="px-4 py-4 text-neutral-600">
                  <div className="flex items-center gap-2">
                    <span>{emp.department}</span>
                    {emp.metadata_sync?.status === "failed" && <PendingSyncBadge testId={`employee-pending-sync-${emp.id}`} />}
                  </div>
                </td>
                <td className="px-4 py-4">
                  {emp.device ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-mono-data bg-neutral-100 border border-neutral-200 rounded-full px-2.5 py-1">
                      <Radio className="w-3 h-3 text-green-600" /> {emp.device.serial_number}
                    </span>
                  ) : revoked ? (
                    <span className="text-xs text-neutral-400">—</span>
                  ) : (
                    <button data-testid={`assign-device-button-${emp.id}`} onClick={() => openAssignDevice(emp)}
                      className="text-xs border border-neutral-300 rounded-full px-3 py-1.5 hover:bg-[#111] hover:text-white hover:border-[#111] transition-colors">
                      Assign device
                    </button>
                  )}
                </td>
                <td className={`px-4 py-4 font-heading text-lg ${scoreTone(emp.latest_score)}`} data-testid={`employee-score-${emp.id}`}>
                  {emp.latest_score ?? "—"}
                </td>
                <td className="px-4 py-4 text-neutral-500 font-mono-data text-xs">{emp.sessions_count}</td>
                <td className="px-4 py-4">
                  <div className="flex items-center justify-end gap-1">
                    {!revoked && (
                      <button data-testid={`revoke-employee-${emp.id}`} onClick={() => revoke(emp)} title="Revoke"
                        className="p-2 rounded-md text-neutral-400 hover:text-amber-600 hover:bg-amber-50 transition-colors">
                        <ShieldOff className="w-4 h-4" />
                      </button>
                    )}
                    {revoked && canPermanentlyDelete(emp) && (
                      <button data-testid={`delete-employee-${emp.id}`} onClick={() => permanentlyDelete(emp)} title="Delete permanently"
                        className="p-2 rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    {revoked && !canPermanentlyDelete(emp) && (
                      <span className="text-[11px] text-neutral-400 px-2 whitespace-nowrap" title="Days until this employee can be permanently deleted">
                        {daysLeft}d left
                      </span>
                    )}
                    <Link to={`/console/employees/${emp.id}`} className="p-2 rounded-md text-neutral-400 hover:text-[#111] hover:bg-neutral-100 transition-colors">
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                </td>
              </tr>
              );
            })}
            {employees?.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-10 text-center text-sm text-neutral-400">No employees yet. Add your first team member.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-employee-modal">
          <DialogHeader>
            <DialogTitle className="font-heading font-medium">Add employee</DialogTitle>
            <DialogDescription>They will be evaluated against their department's SOP.</DialogDescription>
          </DialogHeader>
          <form onSubmit={addEmployee} className="space-y-4 mt-2">
            <div>
              <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">Full name</label>
              <input data-testid="employee-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5 w-full border border-neutral-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#111]" placeholder="Aarav Menon" />
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">Email</label>
              <input data-testid="employee-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 w-full border border-neutral-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#111]" placeholder="aarav@company.com" />
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">Department</label>
              <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })} required>
                <SelectTrigger data-testid="employee-department-select" className="mt-1.5">
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((d) => (
                    <SelectItem key={d.id} value={d.name} data-testid={`department-option-${d.name.toLowerCase().replace(/\s/g, "-")}`}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">Position (optional)</label>
              <input data-testid="employee-position-input" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })}
                className="mt-1.5 w-full border border-neutral-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#111]" placeholder="Guest Relations Executive" />
            </div>
            <button data-testid="employee-submit-button" type="submit" disabled={busy || !form.department}
              className="w-full bg-[#111] text-white rounded-full py-3 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">
              {busy ? "Adding…" : "Add employee"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deviceFor} onOpenChange={(o) => !o && setDeviceFor(null)}>
        <DialogContent className="sm:max-w-md" data-testid="assign-device-modal">
          <DialogHeader>
            <DialogTitle className="font-heading font-medium">Assign pendant — {deviceFor?.name}</DialogTitle>
            <DialogDescription>The device–employee mapping will sync to your local server.</DialogDescription>
          </DialogHeader>
          {unlinkedDevices?.length === 0 ? (
            <p className="text-sm text-neutral-500 mt-2">
              No unlinked devices yet. Devices appear here automatically once a paired tablet syncs — see the{" "}
              <Link to="/console/devices" className="underline underline-offset-4 hover:text-[#111]">Devices tab</Link>.
            </p>
          ) : (
            <form onSubmit={assignDevice} className="space-y-4 mt-2">
              <div>
                <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">Pendant</label>
                <Select value={selectedDeviceId} onValueChange={setSelectedDeviceId} required>
                  <SelectTrigger data-testid="device-select" className="mt-1.5">
                    <SelectValue placeholder={unlinkedDevices === null ? "Loading…" : "Select a device"} />
                  </SelectTrigger>
                  <SelectContent>
                    {unlinkedDevices?.map((d) => (
                      <SelectItem key={d.id} value={d.id} data-testid={`device-option-${d.id}`}>
                        {d.serial_number || d.device_address || d.device_name} · {appLabel(d.app_id)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <button data-testid="device-submit-button" type="submit" disabled={busy || !selectedDeviceId}
                className="w-full bg-[#111] text-white rounded-full py-3 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">
                {busy ? "Assigning…" : "Assign & sync"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
